import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { loadCelloJwtToken as loadCelloJwtTokenAction } from 'redux/modules/cello';

const CELLO_CUSTOM_LAUNCHER_SELECTOR = '.twine-cello-referral-launcher';

function ensureCelloScript(scriptUrl) {
  if (typeof document === 'undefined' || !scriptUrl) {
    return;
  }
  if (document.querySelector(`script[src="${scriptUrl}"]`)) {
    return;
  }
  const script = document.createElement('script');
  script.type = 'module';
  script.src = scriptUrl;
  script.async = true;
  document.head.appendChild(script);
}

function buildProductUserDetails(user) {
  const display = user.displayname || user.username || '';

  return {
    firstName: display,
    email: user.email,
  };
}

/**
 * Loads Cello sandbox Referral Component per quickstart + custom launcher:
 * https://docs.cello.so/referral-component/quickstart
 * https://docs.cello.so/referral-component/custom-launcher
 * Portal "Custom launcher selector" must match `CELLO_CUSTOM_LAUNCHER_SELECTOR` (see Cello custom launcher docs).
 * Requires CELLO_PRODUCT_ID, CELLO_SCRIPT_URL (webpack getEnv), and GET /api/users/{id}/cello-jwt-token → { token }.
 */
function CelloReferralBoot({ user, loadCelloJwtToken }) {
  useEffect(() => {
    if (typeof window === 'undefined' || !user || !user.id) {
      return undefined;
    }

    const productId = process.env.CELLO_PRODUCT_ID;
    const scriptUrl = process.env.CELLO_SCRIPT_URL;
    if (!productId || !scriptUrl) {
      return undefined;
    }

    let cancelled = false;

    loadCelloJwtToken(user.id)
      .then((body) => {
        if (cancelled) {
          return;
        }
        const token = body && body.token;
        if (!token) {
          return;
        }

        window.cello = window.cello || { cmd: [] };
        window.cello.cmd.push(async function (cello) {
          try {
            await cello.boot({
              productId,
              token,
              language: 'en',
              productUserDetails: buildProductUserDetails(user),
              hideDefaultLauncher: true,
              customLauncherSelector: CELLO_CUSTOM_LAUNCHER_SELECTOR,
            });
          } catch (error) {
            console.error('Failed to boot cello:', error);
          }
        });

        ensureCelloScript(scriptUrl);
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Cello: could not load referral token', err);
        }
      });

    return () => {
      cancelled = true;
      if (typeof window === 'undefined') {
        return;
      }
      // Lowercase `window.cello` is the async-load command queue; uppercase `window.Cello` is the
      // runtime API once cello.js runs — both are official (quickstart). Shutdown uses the latter.
      // https://docs.cello.so/referral-component/quickstart
      // https://docs.cello.so/sdk/client-side/cello-js-usage#shutdown
      try {
        if (typeof window.Cello === 'function') {
          window.Cello('shutdown');
        }
      } catch (e) {
        /* ignore */
      }
    };
  }, [
    user?.id,
    user?.email,
    user?.displayname,
    user?.username,
    loadCelloJwtToken,
  ]);

  return null;
}

CelloReferralBoot.propTypes = {
  user: PropTypes.object,
  loadCelloJwtToken: PropTypes.func.isRequired,
};

export default connect(
  (state) => ({ user: state.auth.user }),
  { loadCelloJwtToken: loadCelloJwtTokenAction }
)(CelloReferralBoot);
